/**
 * "Close out the day" — Easy button #4 (memory-bank/plans/easy-buttons.md).
 *
 * Volunteers forget to tap End shift. Stale IN_PROGRESS rows skew every
 * throughput and coverage figure downstream, and the only existing remedy is
 * ending them one at a time, which nobody does.
 *
 * The knock count is the column that matters
 * -------------------------------------------
 * A sweep that closes somebody mid-walk destroys their ability to log the door
 * they are standing in front of — strictly worse than the stale row it was
 * cleaning up. So the server excludes anything younger than the cutoff, and
 * this modal renders each remaining shift's knock count and hours-open so a
 * coordinator can SEE that the one volunteer still working is not in the list,
 * and untick anyone who is.
 *
 * What "closed" means here
 * ------------------------
 * Swept shifts are flagged `ended_by_sweep`. Their `ended_at` is when the
 * coordinator pressed the button, not when the volunteer stopped walking —
 * nobody recorded that, and inventing it would poison every shift-duration
 * figure. The footer says so, because a coordinator reading a report later
 * deserves to know the difference.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, AlertTriangle, Loader2, MoonStar } from 'lucide-react';

import {
  previewCloseStale,
  commitCloseStale,
  easyButtonError,
  type CloseStaleResponse,
} from '@/api/easy';
import ProposalLedger from '@/components/easy/ProposalLedger';

const n = (v: number) => v.toLocaleString();

function isAbortError(err: any): boolean {
  return (
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    err?.code === 'ERR_CANCELED'
  );
}

function ago(hours: number | null): string {
  if (hours == null) return 'unknown';
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

type Props = {
  onClose: () => void;
  onSwept: (closed: number) => void;
};

export default function CloseOutDayModal({ onClose, onSwept }: Props) {
  const [staleHours, setStaleHours] = useState(12);
  /** null = "server's default selection"; a Set = the coordinator has curated. */
  const [deselected, setDeselected] = useState<Set<number>>(new Set());

  const [preview, setPreview] = useState<CloseStaleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      // Deliberately NOT sending shift_ids here: the preview should always show
      // the full eligible population so the ledger stays honest. Deselection is
      // applied client-side for display and only narrows the commit.
      previewCloseStale({ stale_hours: staleHours }, ctrl.signal)
        .then((res) => {
          setPreview(res);
          setError(null);
        })
        .catch((err) => {
          if (isAbortError(err)) return;
          setError(easyButtonError(err, 'Could not check for stale shifts.'));
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(t);
  }, [staleHours]);

  const selectedIds = useMemo(() => {
    if (!preview) return [];
    return preview.proposal.items
      .map((i) => i.shift_id)
      .filter((id) => !deselected.has(id));
  }, [preview, deselected]);

  const toggle = useCallback((id: number) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCommit = useCallback(async () => {
    if (!selectedIds.length) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await commitCloseStale({
        stale_hours: staleHours,
        shift_ids: selectedIds,
      });
      onSwept(res.closed ?? 0);
    } catch (err) {
      setError(easyButtonError(err, 'Could not close the shifts.'));
      setCommitting(false);
    }
  }, [selectedIds, staleHours, onSwept]);

  const nothingToDo = preview !== null && preview.proposal.items.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
      <div className="flex h-full w-full max-w-xl flex-col bg-bg-raised shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border-subtle p-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-fg-primary">
              <MoonStar size={18} /> Close out the day
            </h2>
            <p className="mt-1 text-sm text-fg-secondary">
              Closes shifts somebody forgot to end. Anyone who started recently
              is left alone.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-fg-muted hover:bg-bg-base hover:text-fg-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-status-danger/40 bg-status-danger/10 p-3 text-sm text-status-danger">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {preview && (
            <ProposalLedger
              proposal={preview.proposal}
              noun="open shifts"
              plannedVerb="can be closed"
            />
          )}

          <div>
            <label
              htmlFor="cod-hours"
              className="mb-1 block text-xs font-semibold text-fg-secondary"
            >
              Treat a shift as forgotten after
            </label>
            <div className="flex items-center gap-2">
              <input
                id="cod-hours"
                type="number"
                min={1}
                max={168}
                value={staleHours}
                onChange={(e) => setStaleHours(Number(e.target.value) || 1)}
                className="w-24 rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-primary focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <span className="text-sm text-fg-secondary">hours</span>
            </div>
            <p className="mt-1 text-xs text-fg-muted">
              Generous on purpose. Closing somebody mid-walk stops them logging
              the door they are standing at.
            </p>
          </div>

          {nothingToDo && (
            <div className="rounded-md border border-border-subtle bg-bg-base p-3 text-sm text-fg-secondary">
              Nothing to close — no shift has been open longer than {staleHours}
              h. That is the good outcome.
            </div>
          )}

          {preview && preview.proposal.items.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                Untick anyone who is genuinely still out
              </h3>
              <div className="space-y-1">
                {preview.proposal.items.map((item) => {
                  const checked = !deselected.has(item.shift_id);
                  return (
                    <label
                      key={item.shift_id}
                      className="flex cursor-pointer items-start gap-3 rounded border border-border-subtle bg-bg-base px-3 py-2"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(item.shift_id)}
                        className="mt-1 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-fg-primary">
                          {item.canvasser_name || `Canvasser ${item.canvasser_id}`}
                        </div>
                        <div className="truncate text-xs text-fg-muted">
                          {item.route_name || `Route ${item.route_id}`}
                        </div>
                      </div>
                      <div className="shrink-0 text-right text-xs">
                        <div className="tabular-nums text-fg-secondary">
                          open {ago(item.hours_open)}
                        </div>
                        <div
                          className={
                            item.knock_count > 0
                              ? 'tabular-nums text-status-warning'
                              : 'tabular-nums text-fg-muted'
                          }
                        >
                          {item.knock_count} {item.knock_count === 1 ? 'knock' : 'knocks'}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2 border-t border-border-subtle p-4">
          <p className="text-xs text-fg-muted">
            Closed shifts are marked as ended by this sweep, not by the
            canvasser — so reports can tell an administrative close from a real
            one rather than counting a guessed end time as measured.
          </p>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-fg-secondary">
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Checking…
                </span>
              ) : (
                `${n(selectedIds.length)} selected`
              )}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-border-subtle px-3 py-2 text-sm text-fg-primary hover:bg-bg-base"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCommit}
                disabled={loading || committing || selectedIds.length === 0}
                className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {committing && <Loader2 size={14} className="animate-spin" />}
                Close {selectedIds.length > 0 ? n(selectedIds.length) : ''}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
